import type { MenuObj } from '@modules/professional/component/sub-route-navigation/utils';

export interface ProfessionalNavItem {
    link: string;
    label: string;
    /**
     * True for the Contact tab of a professional with no visible location:
     * there is no location page to show, so the tab opens the enquiry modal
     * instead of navigating.
     */
    opensEnquiry?: boolean;
}

interface BuildProfessionalNavItemsOptions {
    /** The professional has at least one download to show on the Downloads tab. */
    hasDownloads: boolean;
}

/**
 * Turn the shared {@link import('@modules/professional/component/sub-route-navigation/utils').getSubRoutes}
 * tab list into the revamp's nav items.
 *
 * Adds the two tabs the revamp owns on top of the shared list:
 * - Downloads, which only exists in the revamp, sits just before Contact.
 * - Contact, when `getSubRoutes` dropped it because the professional has no
 *   visible location. It stays in the bar (as it does on the legacy header and
 *   mobile nav) and opens the enquiry modal rather than the location page. The
 *   tab itself renders nothing when enquiry CTAs are hidden for the user, so
 *   that check does not belong here.
 *
 * Contact stays last in both cases.
 */
export const buildProfessionalNavItems = (
    items: MenuObj[],
    { hasDownloads }: BuildProfessionalNavItemsOptions,
): ProfessionalNavItem[] => {
    const navItems: ProfessionalNavItem[] = [...items];
    const contactIndex = navItems.findIndex((item) => item.link === '/contact');

    if (hasDownloads) {
        const downloads = { link: '/downloads', label: 'Downloads' };
        if (contactIndex === -1) {
            navItems.push(downloads);
        } else {
            navItems.splice(contactIndex, 0, downloads);
        }
    }

    if (contactIndex === -1) {
        navItems.push({
            link: '/contact',
            label: 'Contact',
            opensEnquiry: true,
        });
    }

    return navItems;
};
